import { forgeInstallRefetchInterval } from "@/lib/jira";
import { workHealthRefetchInterval } from "@/lib/jiraWorkHealth";
import { ApiError, jiraService } from "@/services";
import type { JiraApprovedProject, JiraProjects } from "@/types/api";
import {
	type QueryClient,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { qk } from "./queryKeys";

export function useJiraConnection() {
	return useQuery({
		queryKey: qk.jira,
		queryFn: ({ signal }) => jiraService.getConnection(signal),
		// While the site admin installs the Curo app, watch for it so the card updates by itself.
		refetchInterval: (query) => forgeInstallRefetchInterval(query.state.data),
	});
}

export function useJiraMutations() {
	const qc = useQueryClient();
	return {
		connect: useMutation({ mutationFn: jiraService.startConnection }),
		disconnect: useMutation({
			mutationFn: jiraService.disconnect,
			onSuccess: () => qc.invalidateQueries({ queryKey: qk.jira }),
		}),
	};
}

/** The site's projects and the approved subset. Admin-only on the Gateway, so only an admin's screen calls it. */
export function useJiraProjects() {
	return useQuery({
		queryKey: qk.jiraProjects,
		queryFn: ({ signal }) => jiraService.getProjects(signal),
	});
}

/** The connected site's work and people, for admins. Polls while Curo is still reading tickets. */
export function useJiraWorkHealth() {
	return useQuery({
		queryKey: qk.jiraWorkHealth,
		queryFn: ({ signal }) => jiraService.getWorkHealth(signal),
		refetchInterval: (query) => workHealthRefetchInterval(query.state.data),
	});
}

export function useSetApprovedJiraProjects() {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: jiraService.setApprovedProjects,
		onSuccess: ({ projects }) => applySavedApprovals(qc, projects),
		onError: (error) => refreshAfterFailedSave(qc, error),
	});
}

/** The PUT answers with the new approved list, so the cache takes it without another round trip. */
export function applySavedApprovals(
	qc: QueryClient,
	approved: JiraApprovedProject[],
) {
	qc.setQueryData<JiraProjects>(qk.jiraProjects, (current) =>
		current ? { ...current, approved } : current,
	);
	// A new approval starts an import; a removal takes its project and people out. Not awaited: the save is done.
	void qc.invalidateQueries({ queryKey: qk.jiraWorkHealth });
}

/** Not connected any more, or the site's projects changed: refetch what is now stale. */
export function refreshAfterFailedSave(qc: QueryClient, error: unknown) {
	if (!(error instanceof ApiError)) return;
	if (error.status === 404) return qc.invalidateQueries({ queryKey: qk.jira });
	if (error.code === "unknown_project")
		return qc.invalidateQueries({ queryKey: qk.jiraProjects });
}
